/**
 * TNCY-6 — TanStack Query hooks for the Layer-2 project access surface.
 *
 * Wire (tenancy/v1@1.0.1 — `api.tenancy.gen.ts`):
 *   GET    /api/tenancy/v1/known-tenants                            → listKnownTenants
 *   GET    /api/tenancy/v1/projects/{projectId}/access-grants        → listAccessGrants
 *   POST   /api/tenancy/v1/projects/{projectId}/access-grants        → createAccessGrant
 *   PATCH  /api/tenancy/v1/projects/{projectId}/access-grants/{tenantId} → updateAccessGrant
 *   DELETE /api/tenancy/v1/projects/{projectId}/access-grants/{tenantId} → revokeAccessGrant
 *   GET    /api/tenancy/v1/projects/{projectId}/assignable-users     → listAssignableUsers
 *
 * The whole surface is owner-tenant_admin only on the BE (XT-D7/D23); the
 * UI gates the panel visibility too, but the BE is the security boundary.
 *
 * Reuses the `tenancyFetch` + react-query + optimistic pattern from the
 * Partner Companies feature (`features/tenancy/hooks.ts`) and the admin
 * surface (`features/admin/queries/hooks.ts`).
 *
 * SCOPE NOTE (Christian 2026-06-23): projects/v1 is read-only and there is
 * NO endpoint that writes the descriptive `projects.*_tenant_id` party
 * fields (TNCY-3 left `reconcilePartyGrants` an unwired seam). So the
 * party pickers (PartyPicker.tsx) create the role grant DIRECTLY via
 * `createAccessGrant` (explicit grant) rather than writing a party field +
 * relying on the XT-D23 auto-grant. Writing the party field + auto-grant
 * is a logged follow-up.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { tenancyFetch } from "../../tenancy/tenancyFetch.js";
import { tenancyKeys } from "../../tenancy/keys.js";
import type {
  AccessGrant,
  AccessGrantsResponse,
  AssignableUsersResponse,
  CreateAccessGrantRequest,
  KnownTenantsResponse,
  UpdateAccessGrantRequest,
} from "../../tenancy/types.js";

/**
 * GET /known-tenants — the caller's ACTIVE links (XT-D26). The picker
 * source for every cross-tenant chooser; a grant can never point at a
 * tenant absent from this set (the BE 403s otherwise). Reused by the
 * party pickers + the explicit-grant action.
 *
 * `enabled` lets a closed panel skip the fetch until it opens.
 */
export function useKnownTenantsQuery(enabled = true) {
  return useQuery<KnownTenantsResponse>({
    queryKey: tenancyKeys.knownTenants(),
    enabled,
    queryFn: () => tenancyFetch<KnownTenantsResponse>("/known-tenants"),
    staleTime: 60_000,
    retry: false,
  });
}

/** GET …/access-grants — the project's cross-tenant grants (auto + manual). */
export function useAccessGrantsQuery(
  projectId: string | null | undefined,
  enabled = true,
) {
  const id = projectId ?? "";
  return useQuery<AccessGrantsResponse>({
    queryKey: tenancyKeys.accessGrants(id),
    enabled: enabled && id.length > 0,
    queryFn: () =>
      tenancyFetch<AccessGrantsResponse>(
        `/projects/${encodeURIComponent(id)}/access-grants`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}

export interface CreateAccessGrantArgs {
  projectId: string;
  body: CreateAccessGrantRequest;
}

/**
 * POST …/access-grants — explicit cross-tenant grant. Backs both the
 * party pickers (a friendly front-end over this call) and the panel's
 * "grant a non-party tenant" action (e.g. an `ii_subcontractor`).
 * Invalidates the project's grants list so the new row appears.
 */
export function useCreateAccessGrantMutation() {
  const qc = useQueryClient();
  return useMutation<AccessGrant, Error, CreateAccessGrantArgs>({
    mutationFn: ({ projectId, body }) =>
      tenancyFetch<AccessGrant>(
        `/projects/${encodeURIComponent(projectId)}/access-grants`,
        { method: "POST", body },
      ),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({
        queryKey: tenancyKeys.accessGrants(projectId),
      });
      // A new grant can widen the assignable-users roster.
      void qc.invalidateQueries({
        queryKey: tenancyKeys.assignableUsers(projectId),
      });
    },
  });
}

export interface UpdateAccessGrantArgs {
  projectId: string;
  tenantId: string;
  body: UpdateAccessGrantRequest;
}

/**
 * PATCH …/access-grants/{tenantId} — suspend/resume + show-names toggle
 * (XT-D9/D18). Optimistic: patch the cached grant row so the switch /
 * status chip flips instantly, rolling back on error.
 */
export function useUpdateAccessGrantMutation() {
  const qc = useQueryClient();
  return useMutation<
    AccessGrant,
    Error,
    UpdateAccessGrantArgs,
    { previous: AccessGrantsResponse | undefined }
  >({
    mutationFn: ({ projectId, tenantId, body }) =>
      tenancyFetch<AccessGrant>(
        `/projects/${encodeURIComponent(projectId)}/access-grants/${encodeURIComponent(
          tenantId,
        )}`,
        { method: "PATCH", body },
      ),
    onMutate: async ({ projectId, tenantId, body }) => {
      const key = tenancyKeys.accessGrants(projectId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AccessGrantsResponse>(key);
      if (previous) {
        qc.setQueryData<AccessGrantsResponse>(key, {
          ...previous,
          items: previous.items.map((g) => {
            if (g.tenantId !== tenantId) return g;
            const next: AccessGrant = { ...g };
            if (body.suspended !== undefined) {
              next.status = body.suspended ? "suspended" : "active";
              next.suspendedAt = body.suspended
                ? new Date().toISOString()
                : null;
            }
            if (body.showUserNames !== undefined) {
              next.showUserNames = body.showUserNames;
            }
            return next;
          }),
        });
      }
      return { previous };
    },
    onError: (_err, { projectId }, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(tenancyKeys.accessGrants(projectId), ctx.previous);
      }
    },
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({
        queryKey: tenancyKeys.accessGrants(projectId),
      });
      void qc.invalidateQueries({
        queryKey: tenancyKeys.assignableUsers(projectId),
      });
    },
  });
}

export interface RevokeAccessGrantArgs {
  projectId: string;
  tenantId: string;
}

/** DELETE …/access-grants/{tenantId} — revoke (soft-delete) a grant. */
export function useRevokeAccessGrantMutation() {
  const qc = useQueryClient();
  return useMutation<undefined, Error, RevokeAccessGrantArgs>({
    mutationFn: ({ projectId, tenantId }) =>
      tenancyFetch<undefined>(
        `/projects/${encodeURIComponent(projectId)}/access-grants/${encodeURIComponent(
          tenantId,
        )}`,
        { method: "DELETE" },
      ),
    onSuccess: (_data, { projectId }) => {
      void qc.invalidateQueries({
        queryKey: tenancyKeys.accessGrants(projectId),
      });
      void qc.invalidateQueries({
        queryKey: tenancyKeys.assignableUsers(projectId),
      });
    },
  });
}

/**
 * GET …/assignable-users — users assignable under every granted tenant on
 * a project (XT-D9). Withheld names come back as null first/last + a
 * `placeholder`. Lazy-loaded (the caller passes `enabled`).
 */
export function useProjectAssignableUsersQuery(
  projectId: string | null | undefined,
  enabled = true,
) {
  const id = projectId ?? "";
  return useQuery<AssignableUsersResponse>({
    queryKey: tenancyKeys.assignableUsers(id),
    enabled: enabled && id.length > 0,
    queryFn: () =>
      tenancyFetch<AssignableUsersResponse>(
        `/projects/${encodeURIComponent(id)}/assignable-users`,
      ),
    staleTime: 30_000,
    retry: false,
  });
}
